import { useMemo, useRef, useState } from 'react';
import type { Limits } from '../rules/limits';
import {
  dateTime, daysSince, daysUntil, moneyExact, moneyExactSigned, pct, pctSigned,
  plural, price as fmtPrice, shares as fmtShares, sharesOf, shortDate, todayIso,
} from '../lib/format';
import { accountName, accountNames } from '../lib/accounts';
import { decimal, decimalOr, isPositiveDecimal } from '../lib/number';
import { renameTicker } from '../state/renameTicker';
import { markerTotals, markersFor } from '../state/priceMarkers';
import { basisFromTrades } from '../state/basisFromTrades';
import { isSilenced, silence, silencedEntries, unsilence } from '../state/silenced';
import { CHECKS, kindOf } from '../rules/engine';
import { CapBar } from '../components/CapBar';
import { TickerMark } from '../components/TickerMark';
import { squareDataUrl } from '../lib/image';
import { PriceChart } from '../components/PriceChart';
import { AddToPosition } from '../components/AddToPosition';
import { MoveShares } from '../components/MoveShares';
import { TradeForm } from '../components/TradeLog';
import { FinancialsSection } from '../components/Financials';
import { HideToggle } from '../components/HideToggle';
import { useStore } from '../state/store';
import type { CapKind, Revision, Thesis } from '../types';
import type { Nav } from '../App';

/** The lot's own mark in USD. The position already converted at the listing
    currency, so this reuses that conversion rather than re-deriving it. */
/** Big enough that the tile stays sharp on a dense screen, small enough that a
    dozen of them next to the book cost nothing. */
const LOGO_PX = 96;

function toUsdRate(p: { marketValue: number; shares: number }, _h: unknown): number | null {
  return p.shares > 0 ? p.marketValue / p.shares : null;
}

export function PositionDetail({ ticker, nav }: { ticker: string; nav: Nav }) {
  const {
    book, state, alerts, limits, putThesis, removeThesis, setCap, setLogo, clearLogo,
    saveSettings,
  } = useStore();
  const currency = state.settings.displayCurrency;
  const fx = state.fx;
  const toDisplay = (usd: number) => (currency === 'PLN' && fx?.usdPln ? usd * fx.usdPln : usd);

  const p = book.positions.find((x) => x.ticker === ticker);
  const [editing, setEditing] = useState(false);
  /* This screen had nothing to say anything with, because until now nothing on
     it did something that needed reporting. Recording a purchase does. */
  const [toast, setToast] = useState<string | null>(null);
  const [editingTrade, setEditingTrade] = useState<string | null>(null);
  const logoFileRef = useRef<HTMLInputElement | null>(null);
  const say = (m: string) => { setToast(m); window.setTimeout(() => setToast(null), 3600); };

  const mine = useMemo(
    () => alerts.filter((a) => a.positionId === ticker),
    [alerts, ticker],
  );

  /* Checks this position has been told to stop reporting. Silencing stops the
     card and nothing else: the breach is still a breach and every figure on
     this screen still says so. Undone here for the thesis, and in Settings for
     everything, where the whole list is shown — a setting nobody can find is a
     setting nobody can change. */
  const silencedList = state.settings.silencedChecks;
  const thesisSilenced = isSilenced(silencedList, ticker, 'noThesis');
  const silenceHere = async (kind: string) => {
    await saveSettings({ silencedChecks: silence(silencedList, ticker, kind) });
    /* Points at the nearest way back rather than at settings two screens away:
       the same chip that opened this lists it as OFF, ready to switch on. */
    say(`Off for ${ticker}. Switch it back under Alerts, above.`);
  };
  const unsilenceHere = async (kind: string) => {
    await saveSettings({ silencedChecks: unsilence(silencedList, ticker, kind) });
    say(`${ticker} will be reported again.`);
  };
  const [showChecks, setShowChecks] = useState(false);

  const earnings = state.earnings.find((e) => e.symbol === p?.yahooSymbol);

  /* Recorded fills, plus an opening for any account that has none. Computed
     here rather than in the chart, because the list under it shows the same
     thing and the two must not be able to disagree. */
  const markers = useMemo(
    () => markersFor(ticker, state.data.trades, state.data.holdings),
    [ticker, state.data.trades, state.data.holdings],
  );

  /* Lots whose trades are on record but do not add up to what is held. Their
     average cannot follow the trades, and the screen has to say so. */
  const basisNotes = useMemo(() => {
    const out: string[] = [];
    for (const h of state.data.holdings.filter((x) => x.ticker === ticker)) {
      const check = basisFromTrades(state.data.trades, h);
      if (check.why !== 'shares-differ') continue;
      out.push(
        `In ${accountName(state.data.accounts, h.account)} the trades on record account for `
        + `${fmtShares(check.netShares)} of ${fmtShares(h.shares)} shares.`,
      );
    }
    return out;
  }, [ticker, state.data.trades, state.data.holdings, state.data.accounts]);

  if (!p) {
    return (
      <div className="screen">
        <div className="screen-head">
          <button className="back" onClick={nav.back} aria-label="Back">‹</button>
          <h1>{ticker}</h1>
          <HideToggle push />
        </div>
        <div className="empty">This position is no longer in the book.</div>
      </div>
    );
  }

  const t = p.thesis;

  /* The checks this position could be told to stop reporting: the ones firing
     on it now, the undocumented one whenever its badge would show, and anything
     already switched off here so it can be switched back on. A check silenced
     here produces no alert at all, so the live list alone would never show it
     again — which would make this a one-way switch, and a one-way switch is a
     trap rather than a setting. */
  const switchable = useMemo(() => {
    const kinds = new Set<string>();
    for (const a of mine) kinds.add(kindOf(a.id));
    if (!t || !p.capKind) kinds.add('noThesis');
    for (const e of silencedEntries(silencedList)) {
      if (e.ticker === ticker.toUpperCase()) kinds.add(e.kind);
    }
    return [...kinds]
      .map((kind) => {
        const meta = CHECKS.find((c) => c.kind === kind);
        return {
          kind,
          label: meta?.label ?? kind,
          why: meta?.why ?? '',
          on: !isSilenced(silencedList, ticker, kind),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [mine, t, p.capKind, silencedList, ticker]);

  const mark = state.logos.find((l) => l.symbol === p.yahooSymbol);
  const logo = mark?.dataUrl;
  const ownLogo = mark?.custom === true;
  const riskOver = p.riskToBookPct > limits.riskPerPosition;
  const listingCcy = (p.quote?.currency ?? p.currency) as 'USD';

  // Rewrites since the last report that has actually happened (§7.2).
  const lastEarningsPassed = earnings?.date && daysUntil(earnings.date) < 0
    ? earnings.date
    : null;
  const rewritesSince = (t?.revisions ?? []).filter(
    (r) => !lastEarningsPassed || new Date(r.at) > new Date(`${lastEarningsPassed}T00:00:00Z`),
  ).length;

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>{p.ticker}</h1>
        <span className="dim" style={{ fontSize: 12, marginLeft: 'auto' }}>
          {accountNames(state.data.accounts, p.accounts)}
        </span>
        <HideToggle />
      </div>

      <div className="card detail-head">
        {/* The mark, and a way to set it yourself.
            *
            * The logo service covers a hundred thousand instruments and still
            * misses things — a fund, a Warsaw listing, a coin one exchange
            * carries. For those the only source of a mark is the person who
            * knows which company it is, so the tile is a button: pick a file
            * and it becomes this position's mark, kept on the device like every
            * other one. Clearing it hands the name back to the service. */}
        <input
          ref={logoFileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            void squareDataUrl(file, { px: LOGO_PX, mime: 'image/png' }, 'contain')
              .then((data) => setLogo(p.yahooSymbol, data))
              .then(() => say(`Mark set for ${p.ticker}.`))
              .catch(() => say('That file could not be read as a picture.'));
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button
            onClick={() => logoFileRef.current?.click()}
            aria-label={logo ? `Change the mark for ${p.ticker}` : `Choose a mark for ${p.ticker}`}
            style={{ background: 'none', padding: 0, border: 0, display: 'flex', flex: '0 0 auto' }}
          >
            <TickerMark ticker={p.ticker} color={p.color} size={28} logo={logo} />
          </button>
          <span
            className="dim"
            style={{ fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {p.name ?? p.yahooSymbol}
          </span>
        </div>

        {/* Where the mark came from, and the way to change it — one line, under
            the name rather than beside it, because the name is the long thing
            here and squeezing a button in next to it wrapped both.
            *
            * The domain is worth printing. The lookup matches the symbol
            * exactly, which rules out MU coming back as Murphy Oil, but the
            * service's own symbol-to-company map can still be wrong, and a
            * wrong logo is the one kind of error nothing else on this screen
            * would report. Named, it is checkable at a glance. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
          <span className="dim" style={{ fontSize: 10.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ownLogo ? 'Mark: your own picture'
              : mark?.domain ? <>Mark from <span className="num">{mark.domain}</span></>
              : logo ? 'Mark from the logo service'
              : 'No mark for this one'}
          </span>
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto', flex: '0 0 auto' }}
            onClick={() => logoFileRef.current?.click()}
          >
            {logo ? 'Change' : 'Add one'}
          </button>
          {ownLogo ? (
            <button
              className="btn btn-ghost btn-sm"
              style={{ flex: '0 0 auto' }}
              onClick={() => void clearLogo(p.yahooSymbol).then(() => say('Back to the fetched mark.'))}
            >
              Undo
            </button>
          ) : null}
        </div>

        <div className="detail-value num">{moneyExact(toDisplay(p.marketValue), currency)}</div>

        <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
          <span className="num">{pct(p.weight)} of the book</span>
          {p.costBasis > 0 ? (
            <span className={`num ${p.totalReturnUsd >= 0 ? 'up' : 'down'}`}>
              {p.totalReturnUsd >= 0 ? '▲' : '▼'} {moneyExactSigned(toDisplay(p.totalReturnUsd), currency)}
              {'  '}{pctSigned(p.totalReturnPct)}
            </span>
          ) : (
            <span className="dim">No cost basis recorded</span>
          )}
        </div>

        {/* The shares and the contracts on the same company, read together.
            The weight above counts shares, which is what it has always meant
            and what the ring is drawn from; this is the second reading, and it
            is the one the cap is about. Absent entirely without a contract,
            rather than printed equal to the line above it. */}
        {p.contractCount > 0 ? (
          <div className="dim" style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5 }}>
            <span className="num">{pct(p.exposureWeight)}</span> of the book once
            {' '}{p.contractCount === 1 ? 'the contract counts' : 'the contracts count'}.
            {' '}{p.contractCount === 1 ? 'It adds' : `${plural(p.contractCount, 'contract')} add`}
            {' '}<span className="num">{moneyExact(toDisplay(p.contractNotionalUsd), currency)}</span>
            {' '}of exposure to the same company, bought with margin, so it is not in the value
            above.
          </div>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <CapBar weight={p.weight} cap={p.cap} color={p.color} />
        </div>

        <div className="chips">
          {/* Three states, not two. A ceiling that resolves; none assigned;
              and one assigned to a ceiling the book no longer has — which used
              to fall through to "none set" and assert a different, false fact
              while both cap checks quietly skipped the position. */}
          {p.cap !== null ? (
            <span className="tag" data-kind={p.weight > p.cap ? 'over' : 'ok'}>
              CAP {pct(p.cap)} · {(p.capName ?? '').toUpperCase()}
            </span>
          ) : p.capKind ? (
            <span className="tag" data-kind="warn">SIZE CEILING MISSING</span>
          ) : <span className="tag" data-kind="warn">NO SIZE CEILING SET</span>}
          {/* The badge switches itself off, because this is where it is being
              read and a control you have to go and find is one you will not use.
              It is drawn from the thesis record rather than from any alert, so
              silencing it needs the same decision applied separately — see
              `state/silenced.ts`. */}
          {!t && !thesisSilenced ? (
            <button
              className="tag" data-kind="warn" style={{ cursor: 'pointer' }}
              title={`Stop showing this on ${p.ticker}`}
              onClick={() => void silenceHere('noThesis')}
            >
              NO THESIS ×
            </button>
          ) : null}
          {p.extendedPrice !== null ? (
            <span className="tag" data-kind="muted">
              {p.quote?.marketState === 'PRE' ? 'PRE-MARKET' : 'AFTER HOURS'}
            </span>
          ) : null}
          {p.costIncomplete ? <span className="tag" data-kind="muted">PARTIAL COST BASIS</span> : null}
          {p.valuedAtCost ? <span className="tag" data-kind="warn">VALUED AT COST</span> : null}

          {/* Everything this position is being told about, switchable from the
              row where it is being read rather than from the cards further down
              or from settings two screens away. Present only when there is
              something to switch, so a quiet position keeps a quiet header. */}
          {switchable.length > 0 ? (
            <button
              className="tag" data-kind="muted" style={{ cursor: 'pointer' }}
              aria-expanded={showChecks}
              onClick={() => setShowChecks(!showChecks)}
            >
              ALERTS {switchable.filter((c) => c.on).length}/{switchable.length}
            </button>
          ) : null}
        </div>

        {/* A plain block rather than a card: it opens inside the head card, and a
            card drawn inside a card reads as a box someone forgot to close. */}
        {showChecks ? (
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--edge)',
          }}>
            {switchable.map((c) => (
              <div className="kv" key={c.kind} style={{ alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: c.on ? 'var(--text)' : 'var(--dim)' }}>
                    {c.label}
                  </div>
                  <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 2 }}>
                    {c.why}
                  </div>
                </div>
                <button
                  className="tag" data-kind={c.on ? 'ok' : 'muted'} aria-pressed={c.on}
                  style={{ cursor: 'pointer', padding: '5px 9px', flex: '0 0 auto' }}
                  onClick={() => void (c.on ? silenceHere(c.kind) : unsilenceHere(c.kind))}
                >
                  {c.on ? 'ON' : 'OFF'}
                </button>
              </div>
            ))}
            <div className="dim" style={{ fontSize: 11, lineHeight: 1.45 }}>
              Off stops the card and the badge on {p.ticker} only. Every other position still
              reports it, and none of the figures above change.
            </div>
          </div>
        ) : null}

        <div className="divider" />

        <div className="kv-list">
          <div className="kv">
            {/* Nobody holds 0.35 shares of Bitcoin. */}
            <span className="k">{p.assetKind === 'crypto' ? 'Units' : 'Shares'}</span>
            <span className="v num">{fmtShares(p.shares)}</span>
          </div>
          <div className="kv">
            <span className="k">Average cost</span>
            {/* A price per share, so it prints like the price under it rather
                than like a sum. Everywhere else in the app already treats it
                that way — this row was the one printing it as money, which cost
                it its fourth decimal on a sub-dollar name and made it vanish
                when amounts are hidden. What you paid per share is not how much
                you have, and it is half of what the row below means. */}
            <span className="v num">
              {p.avgCost > 0 ? fmtPrice(p.avgCost, p.currency as 'USD') : 'not recorded'}
            </span>
          </div>
          <div className="kv">
            <span className="k">{p.extendedPrice !== null ? 'Extended-hours price' : 'Price'}</span>
            <span className="v num">{p.priceMissing ? '—' : fmtPrice(p.price, listingCcy)}</span>
          </div>
          {p.extendedPrice !== null ? (
            <div className="kv">
              <span className="k">Regular-hours close</span>
              <span className="v num">{fmtPrice(p.regularPrice, listingCcy)}</span>
            </div>
          ) : null}
          <div className="kv">
            <span className="k">Today</span>
            <span className={`v num ${p.dayChangePct >= 0 ? 'up' : 'down'}`}>
              {p.dayChangePct >= 0 ? '▲' : '▼'} {pctSigned(p.dayChangePct)}
            </span>
          </div>
        </div>

        {p.extendedPrice !== null ? (
          <div className="note" data-tone="warn" style={{ marginTop: 12 }}>
            Extended-hours prices come from thin volume and often do not survive to the open.
          </div>
        ) : null}

        {p.valuedAtCost ? (
          <div className="note" data-tone="warn" style={{ marginTop: 12 }}>
            No mark has come back for {p.yahooSymbol}, so this is being carried at what you paid.
            The value and weight above are estimates, not a market price.
          </div>
        ) : null}

        {p.costIncomplete ? (
          <div className="note" style={{ marginTop: 12 }}>
            {fmtShares(p.shares - p.sharesWithCost)} of these shares have no cost recorded, so the
            return above covers only the {fmtShares(p.sharesWithCost)} that do.
          </div>
        ) : null}

        {p.coinPricedFrom ? (
          <div className="note" style={{ marginTop: 12 }}>
            Yahoo does not list this coin, so it is priced from{' '}
            <span className="num">{p.coinPricedFrom}</span>, the index of MEXC's perpetual on it,
            which is the exchange's reading of what the coin is worth rather than what the contract
            itself trades for. There is no previous close behind it, so there is no day's change to
            show and the price chart has nothing to draw.
          </div>
        ) : null}

        {p.offHoursFrom ? (
          <div className="note" style={{ marginTop: 12 }}>
            The exchange is shut, so this is priced from <span className="num">{p.offHoursFrom}</span>. That is the perpetual MEXC lists on the
            same company, read at its index rather than at what the contract itself trades for. The last regular close was{' '}
            <span className="num">{fmtPrice(p.regularPrice)}</span>, and that is the figure the value
            chart records.
          </div>
        ) : null}

        {listingCcy !== currency && !p.priceMissing ? (
          <div className="note" style={{ marginTop: 12 }}>
            This is listed in {listingCcy} and you are reading the book in {currency}. Profit shown
            in {currency} therefore includes the currency move, which is what your broker shows.
          </div>
        ) : null}
      </div>

      {/* Where the shares actually are. A position spans brokers, and a sale has
          to name one — so the breakdown says how many sit where, what they cost
          there, and what they are worth.
 
          Each lot can also be corrected here. Until it could, the only way to
          fix a share count typed wrong was to record a sale for the difference,
          which put a trade that never happened into the log and a realised gain
          that never existed into the year's figures. A correction is not a
          transaction and this one writes no trade: it says the record was
          wrong, not that something was sold. */}
      {/* Buying more of what is already here.
          *
          * The add-a-position screen is for a company the book has never held:
          * it asks for a thesis and a ceiling that are already written down,
          * and it writes a second lot into an account that already has one.
          * This averages into the lot instead. */}
      <AddToPosition p={p} say={say} />

      {/* Everything you can do to this holding, in one place and at the top.
          `Add to this position` sat here on its own while Add shares, Reduce
          and Close all were at the foot of the screen, past the chart, the
          trades, the accounts and the income statement — so acting on a
          position meant scrolling through everything the screen knows about it
          first. The order is what they cost you: adding, then trimming, then
          the one that ends it. */}
      <div className="fab-row" style={{ marginTop: 9 }}>
        <button className="btn btn-block" onClick={() => nav.go({ name: 'add' })}>Add shares</button>
        <button className="btn btn-block" onClick={() => nav.go({ name: 'reduce', ticker: p.ticker })}>
          Reduce
        </button>
      </div>
      <button className="btn btn-danger btn-block" style={{ marginTop: 9 }}
              onClick={() => nav.go({ name: 'reduce', ticker: p.ticker })}>
        Close all
      </button>

      {/* The price, with the fills on it.
          *
          * An average cost and a mark are two numbers, and no pair of numbers
          * shows whether a position was bought halfway up a run or at the top of
          * one. The line does, at a glance. What is drawn on it is the recorded
          * trades; where an account has none, its lot's opening is drawn as an
          * opening and labelled as an average, because that is what it is. */}
      <PriceChart symbol={p.yahooSymbol} currency={p.currency} markers={markers} avgCost={p.avgCost} />

      {markers.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>What you did</h2>
            <span className="meta">
              {(() => {
                const t = markerTotals(markers);
                const parts = [];
                if (t.buys) parts.push(`${t.buys} bought`);
                if (t.sells) parts.push(`${t.sells} sold`);
                if (t.opened) parts.push(`${t.opened} opened`);
                return parts.join(' · ');
              })()}
            </span>
          </div>
          <div className="rows">
            {[...markers].reverse().map((m, i) => {
              const key = `${m.at}-${m.kind}-${m.account}-${i}`;
              const trade = m.tradeId
                ? state.data.trades.find((t) => t.id === m.tradeId)
                : undefined;

              /* The one being corrected takes the row's place rather than
                 opening below it: the figures being edited are the figures that
                 were on screen, and pushing them out of view to edit them is
                 how a correction gets typed against the wrong row. */
              if (trade && editingTrade === trade.id) {
                return (
                  <TradeForm key={key} trade={trade} say={say}
                             onDone={() => setEditingTrade(null)} />
                );
              }

              const body = (
                <>
                  <div className="row-top">
                    <span className="tag" data-kind={m.kind === 'sell' ? 'over' : m.kind === 'buy' ? 'ok' : 'muted'}>
                      {m.kind === 'opened' ? 'OPENED' : m.kind.toUpperCase()}
                    </span>
                    <span className="num" style={{ fontSize: 13 }}>{shortDate(m.at)}</span>
                    <span className="num" style={{ marginLeft: 'auto', fontWeight: 600 }}>
                      {fmtShares(m.shares)} @ {fmtPrice(m.price, p.currency)}
                    </span>
                  </div>
                  <div className="row-meta">
                    {accountName(state.data.accounts, m.account)}
                    {m.kind === 'opened'
                      ? ' · the day this lot was written down, and the average paid, not a purchase. Correct it below if you know the date.'
                      : m.realizedGain !== undefined
                        ? ` · realised ${moneyExactSigned(m.realizedGain, p.currency as 'USD')}`
                        : m.kind === 'sell' ? ' · no realised gain recorded' : ''}
                    {trade?.fee ? ` · ${moneyExact(trade.fee, p.currency as 'USD')} commission` : ''}
                    {trade ? ' · tap to correct' : ''}
                  </div>
                </>
              );

              /* An opening is not a trade and has no order to open. It is the
                 lot's own record, corrected where the lot is. */
              return trade ? (
                <button key={key} className="row" style={{ width: '100%', textAlign: 'left', display: 'block' }}
                        aria-label={`Correct the ${m.kind} of ${shortDate(m.at)}`}
                        onClick={() => setEditingTrade(trade.id)}>
                  {body}
                </button>
              ) : (
                <div className="row" key={key}>{body}</div>
              );
            })}
          </div>

          {/* Why the average is not simply the trades, when it is not.
              *
              * Where they account for the whole lot the average follows them and
              * there is nothing to explain. Where they account for part of it,
              * the two figures disagree on screen and the reason has to be
              * somewhere — otherwise a corrected trade that does not move the
              * average reads as the correction being ignored. */}
          {basisNotes.length > 0 ? (
            <div className="hint" style={{ marginTop: 10 }}>
              {basisNotes.join(' ')} The average above is the one on the lot, and correcting a trade
              will not move it until the trades account for every share. Correct the lot itself under
              “Where these shares are”.
            </div>
          ) : null}
        </section>
      ) : null}

      <section>
        <div className="section-head">
          <h2>Where these shares are</h2>
          <span className="meta">{p.holdings.length === 1 ? '1 account' : `${p.holdings.length} accounts`}</span>
        </div>
        <div className="rows">
          {[...p.holdings]
            .sort((a, b) => b.shares - a.shares)
            .map((h) => {
              const account = state.data.accounts.find((a) => a.id === h.account);
              const valueUsd = h.shares * (toUsdRate(p, h) ?? 0);
              const gain = h.avgCost > 0 ? h.shares * (p.price - h.avgCost) : null;
              return (
                <Lot key={h.id} holding={h} ticker={p.ticker} say={say}>
                  <div className="row-top">
                    <span className="row-ticker" style={{ fontSize: 13.5, fontWeight: 500 }}>
                      {account?.name ?? h.account}
                    </span>
                    {account?.taxShielded ? (
                      <span className="tag" data-kind="ok">TAX SHIELDED</span>
                    ) : null}
                    {h.avgCost === 0 ? <span className="tag" data-kind="muted">NO COST</span> : null}
                    <span className="row-right">
                      <span className="num" style={{ fontWeight: 600, fontSize: 14 }}>
                        {sharesOf(h.shares, p.ticker)}
                      </span>
                      <span className="num dim" style={{ display: 'block', fontSize: 11.5, textAlign: 'right' }}>
                        {p.priceMissing ? '—' : moneyExact(toDisplay(valueUsd), currency)}
                      </span>
                    </span>
                  </div>
                  <div className="row-meta">
                    <span className="num">
                      {h.avgCost > 0 ? `cost ${fmtPrice(h.avgCost, h.currency as 'USD')}` : 'cost not recorded'}
                    </span>
                    {gain !== null && !p.priceMissing ? (
                      /* The percentage as well as the figure. Each lot was
                         bought at its own price and so has its own return, and
                         the money on its own does not say which lot did well —
                         a big one up a little and a small one up a lot print
                         much the same. It is also what is left to read when
                         amounts are hidden. */
                      <span className={`num ${gain >= 0 ? 'up' : 'down'}`}>
                        {gain >= 0 ? '▲' : '▼'} {moneyExactSigned(gain, h.currency)}
                        {'  '}{pctSigned(((p.price - h.avgCost) / h.avgCost) * 100)}
                      </span>
                    ) : null}
                    <span>{Math.round((h.shares / p.shares) * 1000) / 10}% of the position</span>
                  </div>
                </Lot>
              );
            })}
        </div>
        {p.holdings.some((h) => state.data.accounts.find((a) => a.id === h.account)?.taxShielded) ? (
          <div className="note" style={{ marginTop: 8 }}>
            Gains in a shielded account are exempt from the 19% tax, which is why those shares are
            generally the last ones to sell.
          </div>
        ) : null}
      </section>

      {/* Why this is flagged (§7.2) — only present when a rule actually fires */}
      {mine.length > 0 ? (
        <section>
          <div className="section-head"><h2>Why this is flagged</h2></div>
          <div className="rows">
            {mine.map((a) => (
              <div key={a.id} className="card card-pad"
                   style={{ borderColor: a.severity === 'critical' ? 'var(--crit-edge)' : 'var(--warn-edge)' }}>
                <div className="alert-kind" style={{ color: a.severity === 'critical' ? 'var(--crit)' : 'var(--warn)' }}>
                  <span aria-hidden="true">{a.severity === 'critical' ? '◆' : '▲'}</span>
                  {a.severity === 'critical' ? 'BREACH' : 'WATCH'}
                </div>
                <div className="alert-headline">{a.headline}</div>
                <div className="alert-body">{a.body}</div>
                {/* Guarded on the text rather than on the assignment: a
                    ceiling somebody wrote themselves has no reason, and an
                    empty styled div with its own margin is not an explanation. */}
                {a.id.startsWith('overCap') && p.capReason ? (
                  <div className="alert-body" style={{ marginTop: 8, opacity: 0.85 }}>
                    {p.capReason}
                  </div>
                ) : null}
                {/* Silencing this check on this position only. It stops the
                    card; it does not resolve the breach, correct anything or
                    move a number, and the figures above still say what they
                    said. Which facts are worth being told is the user's call
                    (§1), and this is where it gets recorded. Undone in
                    Settings, where everything silenced is listed. */}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 10 }}
                  onClick={() => void silenceHere(kindOf(a.id))}
                >
                  Stop telling me this about {p.ticker}
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <ThesisSection
        ticker={p.ticker}
        thesis={t}
        capKind={p.capKind}
        weight={p.weight}
        riskToBookPct={p.riskToBookPct}
        riskOver={riskOver}
        editing={editing}
        setEditing={setEditing}
        onSave={putThesis}
        onDelete={() => removeThesis(p.ticker).then(() => say(`Thesis cleared for ${p.ticker}.`))}
        silenced={thesisSilenced}
        onSilence={() => silenceHere('noThesis')}
        onUnsilence={() => unsilenceHere('noThesis')}
        onSetCap={setCap}
        limits={limits}
        rewritesSince={rewritesSince}
        lastEarningsPassed={lastEarningsPassed}
      />

      {/* A coin has no income statement, no EPS and no Rule of 40. The section
          would fetch, get nothing, and draw an empty frame — which reads as a
          request that failed rather than as a question that does not apply. */}
      {p.assetKind === 'crypto'
        ? null
        : <FinancialsSection symbol={p.yahooSymbol} name={p.name} shares={p.shares} />}

      <RenamePosition ticker={p.ticker} nav={nav} />

      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}

/* ---------------- renaming ---------------- */

/* What a position is called, when the company changed its name or the book got
   it wrong at the start.
 *
 * Not a field on the lot editor, because the ticker is not a property of a lot:
 * it is the key eight kinds of record are joined to the position by. Editing it
 * in one place would leave the shares under one name and the thesis, the
 * ceiling, the theme membership, the trades and the dividends under another —
 * two positions where there was one, and no error to say so. So it is its own
 * act, it rewrites the whole book in one transaction, and it says how many
 * records it moved rather than reporting a silent success. */
function RenamePosition({ ticker, nav }: { ticker: string; nav: Nav }) {
  const { state, replaceBook } = useStore();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState(ticker);
  const [busy, setBusy] = useState(false);

  const outcome = open ? renameTicker(state.data, ticker, next) : null;

  const apply = async () => {
    if (!outcome) return;
    setBusy(true);
    try {
      await replaceBook(outcome.data);
      /* Shut before moving. The screen changes its ticker but this panel keeps
         its own state across that, so leaving it open left a rename form on the
         renamed position offering to rename it to what it just became. */
      setOpen(false);
      // The old name no longer names anything, so staying here would be a
      // screen about a position that does not exist.
      nav.go({ name: 'position', ticker: next.trim().toUpperCase() });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 12, width: '100%' }}
              onClick={() => { setNext(ticker); setOpen(true); }}>
        Rename this position
      </button>
    );
  }

  return (
    <div className="card card-pad" style={{ marginTop: 12 }}>
      <div className="section-head" style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 13 }}>Renaming {ticker}</h2>
      </div>

      <div className="field">
        <label htmlFor="rename-to">New name</label>
        <input id="rename-to" className="input" data-mono="true"
               autoCapitalize="characters" spellCheck={false}
               value={next} onChange={(e) => setNext(e.target.value)} />
        <div className="hint">
          What you call the position. The symbol the price service answers to is a separate
          field on each lot above. A company that renamed itself usually changed both, and they
          can move apart.
        </div>
      </div>

      {outcome ? (
        <>
          <div className="kv">
            <span className="k">Records that move</span>
            <span className="v num">{outcome.moved.total}</span>
          </div>
          <div className="dim num" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
            {([
              ['lot', outcome.moved.holdings, 'lots'],
              ['thesis', outcome.moved.theses, 'theses'],
              ['ceiling', outcome.moved.caps, 'ceilings'],
              ['theme', outcome.moved.themes, 'themes'],
              ['trade', outcome.moved.trades, 'trades'],
              ['dividend', outcome.moved.dividends, 'dividends'],
              ['watchlist row', outcome.moved.watchlist, 'watchlist rows'],
              ['past breach', outcome.moved.alertLog, 'past breaches'],
            ] as const)
              .filter(([, n]) => n > 0)
              .map(([one, n, many]) => plural(n, one, many))
              .join(' · ') || 'nothing'}
          </div>
          {outcome.merges ? (
            <div className="note" data-tone="warn" style={{ marginTop: 10 }}>
              Something is already called {next.trim().toUpperCase()}. Renaming joins the two into
              one position, which is right when they are the same company under two names, wrong if they
              are two companies.
            </div>
          ) : null}
        </>
      ) : (
        <div className="hint" style={{ marginTop: 4 }}>
          Type a name that is not the one it already has.
        </div>
      )}

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!outcome || busy}
                onClick={() => void apply()}>
          {busy ? 'Renaming…' : 'Rename everywhere'}
        </button>
      </div>
    </div>
  );
}

/* ---------------- one lot, correctable ---------------- */

/* A lot as it stands, and a way to say it was written down wrong.
 *
 * This is deliberately not the sell screen. Selling records something that
 * happened at a broker; correcting records that the book had the wrong figure
 * for something that did not change. Conflating the two put invented trades in
 * the log — the only way to fix a fat-fingered share count used to be to sell
 * the difference — and invented trades come back as realised gains in the tax
 * figures for the year.
 *
 * So nothing here writes a Trade, and the screen says so where it can be read
 * before the button is pressed rather than after. */
function Lot({ holding, ticker, say, children }: {
  holding: import('../types').Holding;
  ticker: string;
  say: (m: string) => void;
  children: React.ReactNode;
}) {
  const { state, putHolding, removeHolding } = useStore();
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState(String(holding.shares));
  const [avgCost, setAvgCost] = useState(holding.avgCost > 0 ? String(holding.avgCost) : '');
  const [account, setAccount] = useState(holding.account);
  const [symbol, setSymbol] = useState(holding.yahooSymbol);
  const [openedAt, setOpenedAt] = useState(holding.openedAt.slice(0, 10));
  const [confirmRemove, setConfirmRemove] = useState(false);

  const start = () => {
    setShares(String(holding.shares));
    setAvgCost(holding.avgCost > 0 ? String(holding.avgCost) : '');
    setAccount(holding.account);
    setSymbol(holding.yahooSymbol);
    setOpenedAt(holding.openedAt.slice(0, 10));
    setConfirmRemove(false);
    setOpen(true);
  };

  const shareCount = decimal(shares);
  const validShares = Number.isFinite(shareCount) && shareCount > 0;

  const save = async () => {
    if (!validShares) return;
    await putHolding({
      ...holding,
      shares: shareCount,
      /* Blank stays blank. Zero is this book's word for "cost not known", and
         typing a cost in later is the whole point of being able to correct a
         lot — but an empty field must not become a cost of nothing. */
      avgCost: avgCost.trim() === '' ? 0 : Math.max(0, decimalOr(avgCost)),
      account,
      /* The exact symbol the price service answers to, which §3.2 says to store
         rather than guess. Wrong here and no quote ever arrives — the position
         sits at cost forever and nothing says why, which is the failure this
         field exists to be able to fix. */
      yahooSymbol: symbol.trim() || holding.yahooSymbol,
      openedAt: openedAt || holding.openedAt,
    });
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="card card-pad">
        {children}
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={start}>
          Correct this lot
        </button>
        {/* Moving shares is not correcting a record, so it is its own panel
            rather than a field in the correction form. */}
        <MoveShares holding={holding} say={say} />
      </div>
    );
  }

  return (
    <div className="card card-pad">
      <div className="section-head" style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 13 }}>Correcting the record</h2>
      </div>

      <div className="field">
        <label htmlFor={`lot-sh-${holding.id}`}>Shares</label>
        <input id={`lot-sh-${holding.id}`} className="input" data-mono="true" inputMode="decimal"
               value={shares} onChange={(e) => setShares(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor={`lot-c-${holding.id}`}>Average cost ({holding.currency})</label>
        <input id={`lot-c-${holding.id}`} className="input" data-mono="true" inputMode="decimal"
               placeholder="leave blank if you do not know it"
               value={avgCost} onChange={(e) => setAvgCost(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor={`lot-y-${holding.id}`}>Price symbol</label>
        <input id={`lot-y-${holding.id}`} className="input" data-mono="true"
               spellCheck={false} autoCapitalize="characters"
               value={symbol} onChange={(e) => setSymbol(e.target.value)} />
        <div className="hint">
          What the price service calls this listing: bare for a US ticker,
          <span className="num"> EL.PA</span> in Paris, <span className="num">2498.HK</span> in
          Hong Kong. Wrong here and no price ever arrives.
        </div>
      </div>

      <div className="field">
        <label htmlFor={`lot-a-${holding.id}`}>Account</label>
        <select id={`lot-a-${holding.id}`} className="select" value={account}
                onChange={(e) => setAccount(e.target.value)}>
          {state.data.accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`lot-d-${holding.id}`}>Opened</label>
        <input id={`lot-d-${holding.id}`} className="input" type="date"
               value={openedAt} onChange={(e) => setOpenedAt(e.target.value)} />
      </div>

      <div className="note" style={{ marginBottom: 12 }}>
        This changes what the book says you hold. It records no trade and no gain. Use
        Reduce for a sale that actually happened.
      </div>

      {/* The door that has been quietly producing the forbidden shape: two rows
          for one ticker in one account, which stops the average following the
          trades and leaves the second row's shares unsellable. It stays for a
          lot filed against the wrong broker, and refuses where there is already
          a row to collide with. */}
      {account !== holding.account
        && state.data.holdings.some((h) => h.ticker === ticker && h.account === account) ? (
          <div className="note" data-tone="crit" style={{ marginBottom: 12 }}>
            {accountName(state.data.accounts, account)} already holds {ticker}, and one account
            holds one lot. Saving this would leave two rows that each know half the cost. To move
            shares there, use <strong>Move these shares to another account</strong> below, which
            merges them and records what moved.
          </div>
        ) : null}

      <div className="fab-row" style={{ marginTop: 0 }}>
        <button className="btn btn-block" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!validShares} onClick={() => void save()}>
          Save the correction
        </button>
      </div>
      {!validShares ? (
        <div className="hint" style={{ marginTop: 8 }}>
          A share count above zero is needed. To say the lot should not be there at all, remove it.
        </div>
      ) : null}

      <div className="divider" />
      {confirmRemove ? (
        <>
          <div className="note" data-tone="crit">
            Removing takes {sharesOf(holding.shares, ticker)} out of the book with no trade and no
            realised gain. That is right when the lot was entered by mistake and wrong when the
            shares were sold. A sale belongs in Reduce, where it is recorded as one.
          </div>
          <div className="fab-row" style={{ marginTop: 9 }}>
            <button className="btn btn-block" onClick={() => setConfirmRemove(false)}>Keep it</button>
            <button className="btn btn-danger btn-block"
                    onClick={() => void removeHolding(holding.id)}>
              Remove the lot
            </button>
          </div>
        </>
      ) : (
        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(true)}>
          This lot should not be here at all
        </button>
      )}
    </div>
  );
}

/* ---------------- thesis ---------------- */

function ThesisSection({
  ticker, thesis, capKind, weight, riskToBookPct, riskOver, editing, setEditing,
  onSave, onDelete, silenced, onSilence, onUnsilence, onSetCap, limits,
  rewritesSince, lastEarningsPassed,
}: {
  ticker: string;
  thesis: Thesis | undefined;
  capKind: CapKind | null;
  weight: number;
  riskToBookPct: number;
  riskOver: boolean;
  editing: boolean;
  setEditing: (v: boolean) => void;
  onSave: (t: Thesis) => Promise<void>;
  onDelete: () => Promise<void>;
  /** Whether this position has been told to stop reporting a missing thesis. */
  silenced: boolean;
  onSilence: () => Promise<void>;
  onUnsilence: () => Promise<void>;
  onSetCap: (ticker: string, cap: CapKind | null) => Promise<void>;
  limits: Limits;
  rewritesSince: number;
  lastEarningsPassed: string | null;
}) {
  const [why, setWhy] = useState(thesis?.why ?? '');
  const [abortMetric, setAbortMetric] = useState(thesis?.abortMetric ?? '');
  const [abortPrice, setAbortPrice] = useState(thesis?.abortPrice?.toString() ?? '');
  const [checkBy, setCheckBy] = useState(thesis?.checkBy ?? '');
  const [lossIfWrong, setLossIfWrong] = useState(thesis?.lossIfWrong?.toString() ?? '');
  const [earningsPlan, setEarningsPlan] = useState(thesis?.earningsPlan ?? '');
  const [note, setNote] = useState('');
  const [cap, setCapLocal] = useState<CapKind | ''>(capKind ?? '');
  /* The ceilings as they stand, so the picker can never offer one that has been
     deleted in another tab. */
  const bands = useStore().state.data.capBands;

  const overdue = thesis?.checkBy ? daysSince(thesis.checkBy) : 0;
  const projectedRisk = weight * (decimalOr(lossIfWrong) / 100);

  const save = async () => {
    const whyChanged = (thesis?.why ?? '') !== why && (thesis?.why ?? '').trim().length > 0;
    const revisions: Revision[] = [...(thesis?.revisions ?? [])];
    if (whyChanged) {
      // The previous text is kept verbatim, so the history shows what the
      // thinking actually was rather than a summary of it (§5, Revision).
      revisions.push({
        at: new Date().toISOString(),
        note: note.trim() || 'Rewritten without a note.',
        snapshotOfWhy: thesis?.why ?? '',
      });
    }
    const next: Thesis = {
      ticker,
      why: why.trim(),
      abortMetric: abortMetric.trim(),
      checkBy: checkBy || todayIso(),
      lossIfWrong: decimalOr(lossIfWrong),
      revisions,
    };
    if (isPositiveDecimal(abortPrice)) next.abortPrice = decimal(abortPrice);
    if (earningsPlan.trim()) next.earningsPlan = earningsPlan.trim();
    // Saving after the check-by date passed counts as the decision it was
    // waiting for, which is what clears the overdue alert (§4.1).
    if (thesis?.checkBy && daysSince(thesis.checkBy) > 0) next.reviewedAt = new Date().toISOString();
    else if (thesis?.reviewedAt) next.reviewedAt = thesis.reviewedAt;
    if (thesis?.abortCrossedAt) next.abortCrossedAt = thesis.abortCrossedAt;

    await onSave(next);
    await onSetCap(ticker, cap === '' ? null : cap);
    setNote('');
    setEditing(false);
  };

  if (editing) {
    return (
      <section>
        <div className="section-head"><h2>Editing the thesis</h2></div>
        <div className="card card-pad">
          <div className="field field-required">
            <label htmlFor="why">Why I own it</label>
            <textarea id="why" className="textarea" value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="The mechanism. What has to happen in the business for this to work." />
          </div>

          <div className="field field-required">
            <label htmlFor="abort">What proves me wrong</label>
            <textarea id="abort" className="textarea" value={abortMetric}
                      onChange={(e) => setAbortMetric(e.target.value)}
                      placeholder="In business terms, not price terms. What number would end this?" />
          </div>

          <div className="field">
            <label htmlFor="abortPrice">Abort price level (optional)</label>
            <input id="abortPrice" className="input" data-mono="true" inputMode="decimal"
                   value={abortPrice} onChange={(e) => setAbortPrice(e.target.value)} />
            <div className="hint">Setting this makes the book tell you when the mark trades through it.</div>
          </div>

          <div className="field field-required">
            <label htmlFor="checkBy">Check by</label>
            <input id="checkBy" className="input" type="date" value={checkBy}
                   onChange={(e) => setCheckBy(e.target.value)} />
            <div className="hint">A calendar date, not "next quarter".</div>
          </div>

          <div className="field field-required">
            <label htmlFor="loss">How far it falls if you are wrong (%)</label>
            <input id="loss" className="input" data-mono="true" inputMode="decimal" value={lossIfWrong}
                   onChange={(e) => setLossIfWrong(e.target.value)} />
            <div className={`hint ${projectedRisk > limits.riskPerPosition ? 'hint-warn' : ''}`}>
              At {pct(weight)} of the book that costs you {pct(projectedRisk)} of everything.
              Your ceiling per position is {pct(limits.riskPerPosition)}.
            </div>
          </div>

          <div className="field">
            <label htmlFor="cap">Size ceiling</label>
            <select id="cap" className="select" value={cap}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCapLocal(v !== '' && bands.some((b) => b.id === v) ? v : '');
                    }}>
              <option value="">
                {bands.length === 0 ? 'No ceilings defined' : 'No ceiling set'}
              </option>
              {bands.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.pct}%</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="plan">Plan before the next report (optional)</label>
            <textarea id="plan" className="textarea" value={earningsPlan}
                      onChange={(e) => setEarningsPlan(e.target.value)}
                      placeholder="What a miss means. What a beat means. Decided in advance." />
          </div>

          {thesis?.why ? (
            <div className="field">
              <label htmlFor="note">What changed and why</label>
              <input id="note" className="input" value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Kept with the previous wording in the history." />
            </div>
          ) : null}

          <div className="fab-row" style={{ marginTop: 4 }}>
            <button className="btn btn-block" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-primary btn-block" onClick={() => void save()}>Save</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section>
        <div className="section-head">
          <h2>Why I own it</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
            {thesis ? 'Edit' : 'Write it'}
          </button>
        </div>
        <div className="card card-pad">
          {thesis?.why ? (
            <p style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{thesis.why}</p>
          ) : silenced ? (
            /* Said once, quietly, and undoable in place. The alternative to a
               line here is a screen that looks identical whether the check is
               running or switched off, and a setting you cannot see is one you
               will not remember making. */
            <p className="dim" style={{ fontSize: 13, lineHeight: 1.5 }}>
              You said this one does not need a thesis, so it is left out of the undocumented
              count and carries no badge.{' '}
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 2 }}
                onClick={() => void onUnsilence()}
              >
                Ask me for one again
              </button>
            </p>
          ) : (
            <p className="dim" style={{ fontSize: 13, lineHeight: 1.5 }}>
              Nothing written down. A position you cannot explain is one you cannot judge later.
            </p>
          )}

          {/* Two different things, kept apart because they mean different
              things. Clearing throws away what was written; not needing one is
              a decision about a position that never will have one, and a
              holding parked in cash-like size is a fair case for it. Neither
              touches a number. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {thesis ? (
              <button className="btn btn-ghost btn-sm" onClick={() => void onDelete()}>
                Clear the thesis
              </button>
            ) : null}
            {!thesis && !silenced ? (
              <button className="btn btn-ghost btn-sm" onClick={() => void onSilence()}>
                This one does not need a thesis
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section>
        <div className="section-head"><h2>What proves me wrong</h2></div>
        <div className="card card-pad">
          {thesis?.abortMetric ? (
            <p style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{thesis.abortMetric}</p>
          ) : (
            <p className="dim" style={{ fontSize: 13 }}>Not written down yet.</p>
          )}

          <div className="divider" />

          <div className="kv-list">
            <div className="kv">
              <span className="k">Check by</span>
              <span className={`v num ${overdue > 0 ? 'crit' : ''}`}>
                {thesis?.checkBy
                  ? `${shortDate(thesis.checkBy)}${overdue > 0 ? ` · ${overdue}d overdue` : ''}`
                  : 'no date set'}
              </span>
            </div>
            {thesis?.abortPrice ? (
              <div className="kv">
                <span className="k">Abort level</span>
                <span className="v num">{fmtPrice(thesis.abortPrice)}</span>
              </div>
            ) : null}
            <div className="kv">
              <span className="k">Falls by</span>
              <span className="v num">{thesis ? pct(thesis.lossIfWrong) : '—'}</span>
            </div>
            <div className="kv">
              <span className="k">Costs the whole book</span>
              <span className={`v num ${riskOver ? 'crit' : ''}`}>
                {thesis ? pct(riskToBookPct) : '—'}
              </span>
            </div>
          </div>

          {riskOver ? (
            <div className="note" data-tone="crit" style={{ marginTop: 12 }}>
              That is above your ceiling of {pct(limits.riskPerPosition)} for a single position.
              Either the size comes down or the estimate was too pessimistic.
            </div>
          ) : null}

          {thesis?.earningsPlan ? (
            <>
              <div className="divider" />
              <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
                Plan for the next report
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{thesis.earningsPlan}</p>
            </>
          ) : null}
        </div>
      </section>

      <section>
        <div className="section-head">
          <h2>Thesis history</h2>
          {rewritesSince > 0 ? (
            <span className="meta">
              {rewritesSince} {rewritesSince === 1 ? 'rewrite' : 'rewrites'}
              {lastEarningsPassed ? ' since the last report' : ' recorded'}
            </span>
          ) : null}
        </div>
        <div className="card card-pad">
          {(thesis?.revisions?.length ?? 0) === 0 ? (
            <p className="dim" style={{ fontSize: 13 }}>
              No rewrites yet. The original wording still stands.
            </p>
          ) : (
            <div className="timeline">
              {[...thesis!.revisions].reverse().map((r) => (
                <div key={r.at} className="tl-item">
                  <div className="tl-when num">{dateTime(r.at)}</div>
                  <div className="tl-note">{r.note}</div>
                  <div className="tl-prev">Previously: {r.snapshotOfWhy || '(nothing written)'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
